import ArchiveDocumentType from '@Application/Documents/DocumentTypes/Commands/ArchiveDocumentType';
import archiveDocumentType from '@Application/Documents/DocumentTypes/archiveDocumentType';
import ignoreHandledError from '@UI/Helpers/ignoreHandledError';
import documentTypeToArchiveSignal, {
	setDocumentTypeToArchive,
} from '@UI/Pages/DocumentTemplates/OrganizationDocumentTemplates/Signals/documentTypeToArchiveSignal';
import useStrictAuthenticatedUser from '@UI/Providers/AuthenticatedUserProvider/useStrictAuthenticatedUser';
import * as React from 'react';
import { useSignalValue } from '@verclam/pulsar';
import { useDispatch, useSelector } from '@verclam/synapse';
import { ConfirmDialog } from '@verclam/ui-kit';

/**
 * The single confirmation this feature needs: archiving a free type is reversible, so a plain
 * `ConfirmDialog` is enough (no name-retyping `ConfirmDeleteDialog`). Restoring is not destructive and
 * is dispatched directly by `ArchivedDocumentTypeCardActions`, without a dialog of its own. A type is
 * shared by every organization of the account, never scoped to the one the caller is currently on —
 * the message says so.
 */
const ArchiveDocumentTypeConfirmationDialog: React.FC = () => {
	const dispatch = useDispatch();
	const { user } = useStrictAuthenticatedUser();
	const documentTypes = useSelector((state) => state.documentTypesStore.documentTypes);
	const documentTypeToArchiveId = useSignalValue(documentTypeToArchiveSignal);

	const documentTypeToArchive = documentTypes?.findById(documentTypeToArchiveId) ?? null;

	const handleHide = React.useCallback(() => {
		setDocumentTypeToArchive(null);
	}, []);

	const handleAccept = React.useCallback(() => {
		if (user.account === null || documentTypeToArchiveId === null) {
			return;
		}

		dispatch(
			archiveDocumentType(new ArchiveDocumentType(user.account.id, documentTypeToArchiveId)),
		).catch(ignoreHandledError);
		// Closed right away, same reasoning as `RolesConfirmationDialogs`: waiting for the request to
		// settle would leave the dialog open long enough for a second click.
		setDocumentTypeToArchive(null);
	}, [dispatch, user.account, documentTypeToArchiveId]);

	return (
		<ConfirmDialog
			blockScroll
			closable
			draggable={false}
			visible={documentTypeToArchiveId !== null}
			onHide={handleHide}
			header="Archiver ce type de document ?"
			message={
				<p>
					« {documentTypeToArchive?.label ?? ''} » ne pourra plus recevoir de gabarit ni être coché
					<br />
					pour produire un document dans aucune structure du compte, <br />
					tant qu’il reste archivé.
					<br /> <br />
					Les documents déjà produits le conservent. Vous pourrez le désarchiver.
				</p>
			}
			acceptSeverity="danger"
			accept={handleAccept}
			reject={handleHide}
		/>
	);
};

export default ArchiveDocumentTypeConfirmationDialog;
